// Pure helpers for Conversations message templates — categories, placeholder
// resolution and the starter set. Kept free of redux/MUI so the composer, the
// settings CRUD and the tests can all share them.
import { formatDate, nextDue } from "../../../pages/crm/churn-recovery/churnUtils";


// Mirrors CrmRepository::TEMPLATE_CATEGORIES. `key` is what the API stores.
export const TEMPLATE_CATEGORIES = [
  { key: "rebooking", label: "Rebooking", color: "#A72C68" },
  { key: "reminder", label: "Reminder", color: "#0EA5E9" },
  { key: "follow_up", label: "Follow-up", color: "#16A34A" },
  { key: "promotion", label: "Promotion", color: "#F59E0B" },
  { key: "admin", label: "Admin & Billing", color: "#6366F1" },
  { key: "other", label: "Other", color: "#6B7280" },
];

export const categoryMeta = (key) =>
  TEMPLATE_CATEGORIES.find((c) => c.key === key) ||
  TEMPLATE_CATEGORIES[TEMPLATE_CATEGORIES.length - 1];

// Placeholders a staff member can drop into a template body. `resolve` reads
// the context the composer passes in (the customer being messaged).
export const TEMPLATE_VARIABLES = [
  { token: "{{first_name}}", label: "First name", example: "Emma" },
  { token: "{{full_name}}", label: "Full name", example: "Emma Clarke" },
  { token: "{{clinic_name}}", label: "Clinic name", example: "3D Lifestyle" },
  { token: "{{last_service}}", label: "Last service", example: "Hydrafacial" },
  { token: "{{next_date}}", label: "Next appointment", example: "Sep 12, 2026" },
];

const firstNameOf = (name) => `${name || ""}`.trim().split(/\s+/)[0] || "";

/**
 * Swap {{placeholders}} for real values. Unknown tokens and tokens with no
 * value are left verbatim — a visible `{{last_service}}` in the composer is a
 * far better failure than a message that reads "you're due for your next .".
 * Whitespace inside the braces is tolerated ({{ first_name }}).
 */
export const applyTemplateVars = (body, context = {}) => {
  if (!body) return "";
  const values = {
    first_name: firstNameOf(context.name || context.full_name),
    full_name: `${context.name || context.full_name || ""}`.trim(),
    clinic_name: `${context.clinic_name || ""}`.trim(),
    last_service: `${context.last_service || ""}`.trim(),
    next_date: `${context.next_date || ""}`.trim(),
  };
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
    values[key] ? values[key] : match
  );
};

/** Tokens in `body` that this context cannot fill — surfaced as a warning. */
export const unresolvedVars = (body, context = {}) => {
  const resolved = applyTemplateVars(body, context);
  return [...new Set(resolved.match(/\{\{\s*\w+\s*\}\}/g) || [])];
};


/**
 * Resolve placeholder values from what the system already knows about this
 * customer — no hand-typing. `insights` is the churn/customer-insights payload
 * the chat surfaces already load (getCustomerInsights), which carries the last
 * booking and the next expected date. Anything the system can't answer is left
 * empty so applyTemplateVars keeps the token visible instead of inventing text.
 */
export const buildTemplateContext = ({ customerName, clinicName, insights } = {}) => {
  const due = insights ? nextDue(insights) : null;
  const nextDate = due?.date || insights?.cadence?.expected_date || null;
  return {
    name: customerName || "",
    clinic_name: clinicName || "",
    last_service:
      insights?.last_booking?.service || insights?.top_services?.[0]?.name || "",
    next_date: nextDate ? formatDate(nextDate) : "",
  };
};

/** The clinic's own name for {{clinic_name}} — staff logins read it off their provider. */
export const clinicNameOf = (user) =>
  (user?.role_id === 4 ? user?.defaultProvider?.name : user?.name) ||
  user?.defaultProvider?.name ||
  user?.name ||
  "";
/** First line of a body, trimmed for list previews. */
export const templatePreview = (body, max = 90) => {
  const text = `${body || ""}`.replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

/**
 * Starter templates. These are the same rows MessageTemplateSeeder writes, and
 * they double as the read-only fallback the UI shows when the clinic has none
 * yet (or the endpoint isn't deployed) so the picker is never empty. Fallback
 * rows carry a string `id` prefixed "sample-" — the CRUD refuses to edit them.
 */
export const STARTER_TEMPLATES = [
  {
    name: "Rebooking nudge",
    category: "rebooking",
    body: "Hi {{first_name}}, it's {{clinic_name}} — you're due for your next {{last_service}}. Want me to hold a spot for you this week?",
  },
  {
    name: "Overdue win-back",
    category: "rebooking",
    body: "Hi {{first_name}}, we haven't seen you in a while and wanted to check in. Your last results looked great — shall we get you back on the books?",
  },
  {
    name: "Appointment reminder",
    category: "reminder",
    body: "Hi {{first_name}}, just a reminder about your appointment at {{clinic_name}} on {{next_date}}. Reply YES to confirm or call us to reschedule.",
  },
  {
    name: "Day-before confirmation",
    category: "reminder",
    body: "Hi {{first_name}}, we're looking forward to seeing you tomorrow at {{clinic_name}}. Please arrive 10 minutes early and skip makeup if you can.",
  },
  {
    name: "Post-treatment check-in",
    category: "follow_up",
    body: "Hi {{first_name}}, how are you feeling after your {{last_service}}? Let us know if you have any questions — we're here for you.",
  },
  {
    name: "Review request",
    category: "follow_up",
    body: "Hi {{first_name}}, so glad you enjoyed your visit at {{clinic_name}}! If you have a minute, a quick Google review really helps us out.",
  },
  {
    name: "Monthly promo",
    category: "promotion",
    body: "Hi {{first_name}}, this month at {{clinic_name}} we're running a special on your favourite treatment. Reply INFO and I'll send the details.",
  },
  {
    name: "Unused wallet credit",
    category: "promotion",
    body: "Hi {{first_name}}, you still have credit sitting on your {{clinic_name}} account. Want to put it toward a treatment this month?",
  },
  {
    name: "Membership renewal",
    category: "admin",
    body: "Hi {{first_name}}, your {{clinic_name}} membership renews soon. Everything still working for you, or would you like to adjust the plan?",
  },
  {
    name: "Payment method declined",
    category: "admin",
    body: "Hi {{first_name}}, the card on file for your {{clinic_name}} membership was declined. Could you update it when you get a chance?",
  },
];

export const SAMPLE_ID_PREFIX = "sample-";

export const isSampleTemplate = (template) =>
  typeof template?.id === "string" && template.id.startsWith(SAMPLE_ID_PREFIX);

/** The starter set shaped like API rows, for the empty/offline fallback. */
export const sampleTemplates = () =>
  STARTER_TEMPLATES.map((t, index) => ({
    ...t,
    id: `${SAMPLE_ID_PREFIX}${index + 1}`,
    is_active: true,
    sort_order: index,
    author_name: null,
    updated_at: null,
    is_sample: true,
  }));

/** Active templates grouped by category, in TEMPLATE_CATEGORIES order. */
export const groupByCategory = (templates = []) =>
  TEMPLATE_CATEGORIES.map((category) => ({
    ...category,
    templates: templates.filter((t) => (t.category || "other") === category.key),
  })).filter((group) => group.templates.length > 0);
